import React, { useContext, useEffect, useRef, useState } from 'react';
import Bundle from '../../models/Bundle';
import OrderPayload from '../../models/OrderPayload';
import { SalesContext } from '../../SalesContext';
import { replace, sleep } from '../../utils';
import { Button } from '../core/button';
import { SignAgreementModal } from '../agreement/sign_agreement/SignAgreementModal';
import { BundleForm } from './bundle_form';
import { BundleTable } from './bundle_table';
import { createOrder as createOrderService, uploadOrderFile, updateAgreementMetadata, fetchProducts, attachQcRecording, updateAgreementWithNewCityAndDate } from './service';
import { Collapse } from 'react-collapse';
import Notification from '../core/notification';
import styles from './order.module.scss';
import { AgreementStatusIndicator } from '../agreement/agreement_status';
import { localizedJoin } from '../../utils/strings';
import { AgreementStatus } from '../../models/AgreementMetadata';
import { fetchAgreementMetadata } from '../sign_agreement/service';
import { QCModal } from '../qc/QCModal';
import { AgreementDistributionPopover } from '../agreement/distribute_agreement_popover';
import moment from 'moment';
import Select from '../core/select';
import { Field } from '../core/field';
import { Modal } from '../core/modal';
import { Busy } from '../core/busy';
import { useDetectIosDevice } from '../agreement/sign_agreement/hooks';

/**
 * View for inputting order information.
 */
export const OrderView: React.FC = () => {
  const {
    userToken,
    agreement,
    agreementMeta,
    setAgreement,
    setAgreementMeta,
    setOrderIds,
    creditCheckResult,
    monthlyPaymentTotal,
    config,
    bundles,
    setBundles,
    qcRecording,
    products,
    setProducts,
    offerSignatureUri,
    isDemo
  } = useContext(SalesContext);

  const rootNode = useRef<HTMLDivElement>(null);
  const isMetaAutoRefreshEnabled = useRef(false);
  const [fetchingStockGroups, setFetchingProducts] = useState(false);
  const [createOrderBusy, setCreateOrderBusy] = useState(false);
  const [signAgreementOpen, setSignAgreementOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [distributeAgreementOpen, setDistributeAgreementOpen] = useState(false);
  const [updateCityOpen, setUpdateCityOpen] = useState(false);
  const [updatedCity, setUpdatedCity] = useState<string>("");
  const [distributedAgreement, setDistributedAgreement] = useState(false);
  const [bundleInput, setBundleInput] = useState(new Bundle());
  const [qcRecordingUploadError, setQcRecordingUploadError] = useState<any>(undefined);
  const [photo, setPhoto] = useState<Blob>(null);
  const [isUpdatingCity, setUpdatingCity] = useState(false);
  const [isUploadingPhoto, setUploadingPhoto] = useState(false);

  const [shouldFetch, setShouldFetch] = useState(false);

  const openDistributionOnQcClose = useRef(true);

  const supportsPdfViewer = useRef(!useDetectIosDevice());

  useEffect(() => {
    if (!creditCheckResult) {
      return;
    }
    const bundle = new Bundle({
      address: creditCheckResult.address.split(",").shift(),
      city: creditCheckResult.city,
      postalCode: creditCheckResult.zip,
    })
    setBundleInput(bundle);
  }, [JSON.stringify(creditCheckResult)])

  async function getProducts() {
    if (fetchingStockGroups) {
      return;
    }
    setFetchingProducts(true);

    const products = await fetchProducts();

    setProducts(products);
    setFetchingProducts(false);
  }

  async function autoRefreshAgreementMeta() {
    do {
      try {
        var meta = await fetchAgreementMetadata(agreement.key);
        setAgreementMeta(meta);
        await sleep(meta.isSigned ? 8000 : 1500); //  Wait longer when signed, as we do not expect a status change
      } catch (e) {
        console.error(e);
        await sleep(4000);
      }
    } while (isMetaAutoRefreshEnabled.current);
  }

  async function updateAgreementCity() {
    setUpdatingCity(true);
    try {
      await updateAgreementMetadata(agreement.key, {
        signCity: updatedCity
      });

      await updateAgreementWithNewCityAndDate(agreement.key, {
        offerSignatureUri,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setUpdateCityOpen(false);
      setUpdatingCity(false);
      Notification.show("Uppdaterade ort")
    }
  }

  /**
   * Creates a SaleSys order.
   */
  async function createOrder(phoneNumber: string) {
    if (createOrderBusy) {
      return;
    }

    setCreateOrderBusy(true);

    let orderBundles = bundles;

    try {
      var {orderId, serialId, fileToken} = await createOrderService(new OrderPayload({
        creditCheckResult,
        agreement,
        bundles: orderBundles,
        agreementMeta
      }), config, phoneNumber);
    } catch (e) {
      console.error(e);
      Notification.show("Kunde inte lägga order");
    }

    if (orderId) {
      setOrderIds([orderId]);
      Notification.show(`Lagt order ${orderId}`);
    }

      //  No need to awaitFTODO
      updateAgreementMetadata(agreement.key, { orderId, orderSerialId: serialId })

      if (qcRecording) {
        try {
          try {
            await attachQcRecording(agreement.key, orderId, fileToken);
          } catch (error1) {
            console.error("alert: Error attaching agreement %s pre-uploaded QC", agreement?.key, error1);

            await uploadOrderFile(orderId, fileToken, {
              file: qcRecording,
              fileName: 'Kvalitetskontroll.ogg'
            });
          }

          setQcRecordingUploadError(null);
        } catch (error) {
          alert("Inspelningen för kvalitetskontroll kunde inte bifogas. Du måste klicka på knappen för att ladda ner ljudfilen.");
          setQcRecordingUploadError(error);
          console.error("alert: Error attaching agreement %s QC", agreement?.key, error);
          Notification.show(`Kunde inte bifoga QC i order ${serialId}`);
        }
      }

      try {
        await Promise.all([
          uploadOrderFile(orderId, fileToken, {
            fileUrl: agreement.uri,
            fileName: 'Avtal.pdf'
          })
        ])
        Notification.show(`Bifogat avtal i order ${serialId}`);
      } catch (e) {
        console.error(e);
        Notification.show(`Kunde inte bifoga avtal  i order ${serialId}`);
      }

      setCreateOrderBusy(false);

      await sleep(7000);
      window.location.reload();
  }

  useEffect(() => {
    if (userToken) {
      getProducts();
    }
  }, [userToken]);

  useEffect(() => {
    if (!agreement) {
      setDistributedAgreement(false);
    }
  }, [agreement]);

  useEffect(() => {
    if (!agreement?.key) {
      return;
    }

    isMetaAutoRefreshEnabled.current = true;
    autoRefreshAgreementMeta();
    return () => { isMetaAutoRefreshEnabled.current = false; }
  }, [agreement?.key]);

  useEffect(() => {
    if (agreementMeta?.status === AgreementStatus.Signed && signAgreementOpen) {
      setSignAgreementOpen(false);
      setQcOpen(true);
    }
  }, [agreementMeta?.status]);

  const canCreateAgreement = creditCheckResult != null && creditCheckResult.credibility === "1" && bundles?.length > 0;
  return (
    <div ref={e => {
      rootNode.current = e;
    }} id="order" className="view">
      {creditCheckResult && creditCheckResult?.credibility === "1" ?
        <div className={styles.content}>
          <div>
            <Collapse isOpened>
              <BundleTable
                disabled={agreement != null}
                bundles={bundles || []}
                onBundlesChange={setBundles}
                onBundleSelect={bundle => {
                  if (bundle.key === bundleInput?.key) {
                    setBundleInput(new Bundle());
                  } else {
                    setBundleInput(bundle);
                  }
                }}
                selectedBundleKey={bundleInput?.key} />
            </Collapse>

            <BundleForm
              bundle={bundleInput}
              bundleCount={bundles.length}
              maxBundleCount={4}
              onChange={bundle => {
                setBundleInput(bundle);
                setBundles(replace(bundles, bundle, 'none', 'key'));
              }}
              disabled={agreement != null}
              energyItems={products}
              onSubmit={bundle => {
                if (bundles.length > 4) {
                  Notification.show("Du kan endast ha max 4 produkter per kund.");
                  return;
                }
                setBundles([...bundles, bundle]);
                setBundleInput(new Bundle({
                  address: bundle.address,
                  city: bundle.city,
                  postalCode: bundle.postalCode
                }));
              }}
            />
          </div>
          <div className={styles.actions}>
            {
              agreement ? (
                <>
                  <AgreementDistributionPopover
                    open={distributeAgreementOpen}
                    onClose={() => setDistributeAgreementOpen(false)}
                    agreement={agreement}
                    onAgreementDistributed={(phoneNumber) => {
                      setDistributedAgreement(true);
                      setDistributeAgreementOpen(false);
                      createOrder(phoneNumber);
                    }}
                  />

                  <Modal open={updateCityOpen} onClose={() => setUpdateCityOpen(false)} children={
                    !isUpdatingCity ?
                      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <h3>Uppdatera ort</h3>
                        <Field label="Ny ort" onTextValueChange={(value) => setUpdatedCity(value)} />
                        <div style={{ textAlign: "center" }}>
                          <Button weight='primary' busy={isUpdatingCity} onClick={() => updateAgreementCity()}>Spara</Button>
                        </div>
                        <p style={{ textAlign: "center" }}>Avtalets signeringsort uppdateras med den nya orten</p>
                      </div> :
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Busy />
                        <p>Uppdaterar ort</p>
                      </div>
                  }
                  />

                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", width: "100%", gap: "2rem" }}>
                    {agreementMeta?.status === AgreementStatus.Signed ?
                      <>
                        <Button
                          type="submit"
                          onClick={() => setDistributeAgreementOpen(true)}
                          weight={distributedAgreement ? "normal" : "primary"}
                          disabled={[AgreementStatus.Canceled, AgreementStatus.Declined, AgreementStatus.Revised].includes(agreementMeta?.status) || !qcRecording}>
                          Skicka avtalskopia
                        </Button>

                        <Button
                          type="submit"
                          onClick={() => setUpdateCityOpen(true)}
                          weight="normal"
                          disabled={[AgreementStatus.Canceled, AgreementStatus.Declined, AgreementStatus.Revised].includes(agreementMeta?.status) || !qcRecording}>
                          Uppdatera ort
                        </Button>
                      </> : null
                    }

                    {
                      qcRecordingUploadError === undefined ? (
                        <Button type="submit" onClick={() => setQcOpen(true)}>
                          Öppna kvalitetskontroll
                        </Button>
                      ) : qcRecordingUploadError ? (
                        <Button weight="primary" download={`qc_${creditCheckResult.ssn || ''}.ogg`} externalLinkTo={URL.createObjectURL(qcRecording)}>
                          Ladda ner inspelning
                        </Button>
                      ) : null
                    }
                  </div>
                  {
                    agreement?.key ? (
                      <AgreementStatusIndicator
                        className={styles.status}
                        agreement={agreement}
                        meta={agreementMeta}
                        offerUri={agreement.uri}
                      />
                    ) : null
                  }
                </>
              ) : (
                <>
                  <div style={{ position: "relative", display: "flex", flexDirection: "row", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", flexDirection: "row", gap: "1rem", justifyContent: "space-between", width: "100%" }}>
                      {!photo ?
                        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                          <label style={{ position: 'relative', zIndex: 100 }}>
                            <input
                              type="file"
                              style={{
                                opacity: 0,
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 10
                              }}
                              disabled={(!canCreateAgreement || isUploadingPhoto) && !isDemo}
                              accept="image/*"
                              onChange={event => {
                                const file = event.currentTarget.files[0];
                                if (!file) {
                                  return;
                                }

                                setUploadingPhoto(true);

                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  const img = new Image();
                                  img.onload = () => {
                                    const canvas = document.createElement('canvas');
                                    const ctx = canvas.getContext('2d');

                                    const maxWidth = 1200;
                                    const scale = Math.min(1, maxWidth / img.width);
                                    canvas.width = img.width * scale;
                                    canvas.height = img.height * scale;

                                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                                    canvas.toBlob((blob) => {
                                      setPhoto(blob);
                                      setUploadingPhoto(false);
                                    }, 'image/jpeg', 0.8);
                                  };
                                  img.src = e.target.result as string;
                                };
                                reader.readAsDataURL(file);
                              }} />
                            <Button
                              type="submit"
                              style={{
                                width: '100%',
                                zIndex: -1  //  Går inte att klicka på <label>
                              }}
                              onClick={() => { }}
                              disabled={(!canCreateAgreement || isUploadingPhoto) && !isDemo}
                              weight="primary">
                              Ladda upp ID-kort
                            </Button>
                          </label>
                        </div>

                        :
                        <>
                          <Button
                            type="submit"
                            onClick={() => setPhoto(null)}
                            disabled={!canCreateAgreement || isUploadingPhoto}
                            weight="normal">
                            Ta bort ID-kort
                          </Button>
                          <Button
                            type="submit"
                            onClick={() => {
                              setShouldFetch(true);
                              setSignAgreementOpen(true);
                            }}
                            weight={"primary"}
                            disabled={(!canCreateAgreement || isUploadingPhoto || !photo) && !isDemo}>
                            Skapa avtal, signera nu
                          </Button>
                        </>
                      }
                    </div>
                  </div>
                </>
              )
            }

            <SignAgreementModal
              open={signAgreementOpen}
              onClose={() => setSignAgreementOpen(false)}
              creditCheckResult={creditCheckResult}
              bundles={bundles}
              photo={photo}
              onSigned={agreement => setAgreement(agreement)}
              shouldFetch={shouldFetch}
              shouldRenderPdf={supportsPdfViewer.current}
            />
            <QCModal
              open={qcOpen}
              onClose={() => {
                setQcOpen(false);

                if (openDistributionOnQcClose.current) {
                  openDistributionOnQcClose.current = false;
                }
              }}
            />
          </div>
        </div>
        : <div>
          <h2>Saknas giltigt kreditupplysningsinformation</h2>
        </div>}
    </div>
  );
}